import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListSegmentsResult } from '@whinerp/client';
import { EmptyState, ErrorState, InlineAction, Num, Skeleton, type Column } from '@whinerp/ui';
import { formatCount, formatRelativeDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { RecordList } from '../shared/record-list.tsx';
import { nextSort } from '../shared/use-saved-view.js';

/**
 * SC-50 Audiences.
 *
 * The segment builder existed and there was no list: a saved audience was
 * reachable only by typing its URL, and nothing could save one in the first
 * place. A record type with no list is a record type nobody can find.
 *
 * **The count is never shown on its own.** `memberCount` arrives beside
 * `countedAt` and is null unless both are known, and this screen renders the
 * pair - "1,284 · counted 3 days ago" - because a number with no time on it is
 * one people read as current. The figure on this list, the figure on the
 * builder and the audience a campaign sends to all come from one predicate; a
 * stale figure presented as a live one is the only way they can still appear
 * to disagree.
 */

type Row = ListSegmentsResult['segments'][number];

function SegmentsListScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({
    field: 'created',
    direction: 'desc',
  });
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const query = useQuery<ListSegmentsResult, ApiError>({
    queryKey: ['segments'],
    queryFn: () => api.listSegments({}),
  });

  const recount = useMutation({
    mutationFn: (segmentId: string) => api.countSegmentNow({ path: { segmentId } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['segments'] }),
  });
  // Keeps who matches now as a list that will not change (G-168).
  const freeze = useMutation({
    mutationFn: (segmentId: string) => api.freezeSegment({ path: { segmentId }, body: {} }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['segments'] }),
  });

  /*
   * Filtered and sorted here, not by the server.
   *
   * A workspace has tens of audiences, not tens of thousands: paging them
   * would be a cursor, a total and a page size to describe a list that fits on
   * one screen. The toolbar's search is honest about what it does - it
   * narrows what is already loaded - and the footer says the same count the
   * list holds rather than a server total that would be the same number.
   */
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = query.data?.segments ?? [];
    const found = term === '' ? all : all.filter((row) => row.name.toLowerCase().includes(term));
    const direction = sort.direction === 'asc' ? 1 : -1;
    return [...found].sort((a, b) => {
      if (sort.field === 'name') return a.name.localeCompare(b.name) * direction;
      if (sort.field === 'count') {
        // Never counted sorts last in both directions, like a null in a list:
        // "no number yet" is not a small number.
        if (a.memberCount === null) return 1;
        if (b.memberCount === null) return -1;
        return (a.memberCount - b.memberCount) * direction;
      }
      return a.createdAt.localeCompare(b.createdAt) * direction;
    });
  }, [query.data, search, sort]);

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'name',
        header: t('segment.list.name'),
        sortField: 'name',
        essential: true,
        cell: (row) => (
          <span className="flex items-baseline gap-2">
            <span>{row.name}</span>
            {/* Said beside the name: a frozen list and a live rule answer different questions. */}
            <span data-segment-kind={row.kind} className="text-meta text-ink-meta">
              {row.kind === 'static' ? t('segment.kind.static') : t('segment.kind.live')}
            </span>
          </span>
        ),
      },
      {
        key: 'count',
        header: t('segment.list.count'),
        sortField: 'count',
        align: 'end',
        essential: true,
        cell: (row) =>
          /*
           * Keyed off the timestamp alone, not off both.
           *
           * The API guarantees `memberCount` is null whenever `countedAt` is -
           * a count with no time on it is never reported - so a second check
           * here would be a branch nothing can reach and no test can exercise.
           * One condition, in the one place that decides it.
           *
           * "Not counted yet" rather than a zero: nobody in this audience and
           * nobody has counted it are different answers, and only one of them
           * is a reason not to send.
           */
          row.countedAt === null ? (
            <span data-segment-uncounted={row.segmentId} className="text-ink-300">
              {t('segment.list.neverCounted')}
            </span>
          ) : (
            <span className="flex items-baseline justify-end gap-2">
              <Num align="end" data-segment-count={row.segmentId} className="text-ink">
                {formatCount(row.memberCount ?? 0, format)}
              </Num>
              <Num data-segment-counted-at={row.segmentId} className="text-meta text-ink-meta">
                {formatRelativeDate(row.countedAt, format)}
              </Num>
            </span>
          ),
      },
      {
        key: 'recount',
        header: t('segment.list.recount'),
        align: 'end',
        hideOnCard: true,
        cell: (row) => (
          <InlineAction
            data-segment-recount={row.segmentId}
            disabled={recount.isPending && recount.variables === row.segmentId}
            onClick={() => recount.mutate(row.segmentId)}
          >
            {t('segment.list.recount')}
          </InlineAction>
        ),
      },
      {
        key: 'freeze',
        header: t('segment.list.freeze'),
        align: 'end',
        hideOnCard: true,
        cell: (row) =>
          row.kind === 'live' ? (
            <InlineAction
              data-segment-freeze={row.segmentId}
              disabled={freeze.isPending && freeze.variables === row.segmentId}
              onClick={() => freeze.mutate(row.segmentId)}
            >
              {t('segment.list.freeze')}
            </InlineAction>
          ) : null,
      },
    ],
    [t, format, recount, freeze],
  );

  return (
    <RecordList<Row>
      title={t('segment.list.title')}
      icon="UsersThree"
      rowId={(row) => row.segmentId}
      recordHref={(row) => `/segments/${row.segmentId}`}
      columns={columns}
      page={{ rows, total: rows.length, hasMore: false, nextCursor: null }}
      loading={query.isPending}
      error={query.isError ? { message: query.error.message, rule: query.error.rule } : null}
      onRetry={() => void query.refetch()}
      refreshing={query.isFetching}
      search={search}
      onSearchChange={setSearch}
      sort={sort}
      onSortChange={(field) => setSort((current) => nextSort(current, field))}
      cursorStack={cursorStack}
      onCursorStackChange={setCursorStack}
      pageSize={rows.length || 1}
      onNew={() => navigate('/segments/new')}
      onClearFilters={() => setSearch('')}
      filtered={search.trim().length > 0}
      emptyTitle={t('segment.list.empty.title')}
      emptyBody={t('segment.list.empty.body')}
      emptyIllustration="UsersThree"
    />
  );
}

function SegmentsEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="UsersThree"
      title={t('segment.list.empty.title')}
      body={t('segment.list.empty.body')}
      primary={{ label: t('segment.list.new'), icon: 'Plus', href: '/segments/new' }}
    />
  );
}

function SegmentsLoading(): JSX.Element {
  return (
    <div className="flex flex-col gap-px" role="status" aria-busy="true">
      <span className="sr-only">Loading audiences</span>
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className="h-row-comfortable" />
      ))}
    </div>
  );
}

function SegmentsError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  const t = useT();
  return (
    <ErrorState
      title={t('state.error')}
      body={error instanceof Error ? error.message : 'The audiences did not load.'}
      onRetry={retry}
      retryLabel={t('action.retry')}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-50',
  route: '/segments',
  surface: 'page',
  title: 'Segments list',
  features: ['F-063'],
  entitlement: 'F-063',
  reachedBy: [{ kind: 'nav', item: 'segments' }],
  components: ['DataTable', 'FilterChips'],
  elements: ['RecordList'],
  states: {
    empty: SegmentsEmpty,
    loading: SegmentsLoading,
    error: SegmentsError,
  },
  component: SegmentsListScreen,
});

export default screen;
